// PartCPage2.jsx - Part C Page 2 for HPC Report (Pixel-perfect redesign)
import React from 'react';

const PartCPage2 = ({ data }) => {
  const studentData = data || {};

  // Full-width chevron box component
  const ChevronFullBox = ({ labelText, children, showArrow = false }) => (
    <div style={{ position: 'relative', marginBottom: '10px' }}>
      {showArrow && (
        <div style={{
          position: 'absolute',
          left: '50%',
          top: '-22px',
          transform: 'translateX(-50%)',
          fontSize: '28px',
          color: '#4A90A4',
          fontWeight: 'bold'
        }}>
          ↓
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'stretch' }}>
        <div style={{
          backgroundColor: '#4A90A4',
          color: 'white',
          padding: '15px 20px',
          fontSize: '11px',
          fontWeight: 'bold',
          clipPath: 'polygon(0 0, 85% 0, 100% 50%, 85% 100%, 0 100%)',
          minWidth: '220px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          lineHeight: '1.3'
        }}>
          {labelText}
        </div>
        <div style={{
          backgroundColor: '#B3D9E8',
          flex: 1,
          padding: '12px 15px',
          minHeight: '100px',
          marginLeft: '-15px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          fontSize: '10px',
          lineHeight: '1.4'
        }}>
          {children || '-'}
        </div>
      </div>
    </div>
  );

  // Half-width chevron box for side-by-side layout
  const ChevronHalfBox = ({ labelText, children }) => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{
        backgroundColor: '#4A90A4',
        color: 'white',
        padding: '10px 15px',
        fontSize: '11px',
        fontWeight: 'bold',
        textAlign: 'center',
        lineHeight: '1.3'
      }}>
        {labelText}
      </div>
      <div style={{
        backgroundColor: '#B3D9E8',
        flex: 1,
        padding: '12px 15px',
        minHeight: '120px',
        fontSize: '10px',
        lineHeight: '1.4',
        border: '2px solid #4A90A4',
        borderTop: 'none'
      }}>
        {children || '-'}
      </div>
    </div>
  );

  return (
    <div className="report-page" style={{
      width: '210mm',
      minHeight: '297mm',
      padding: '20mm',
      backgroundColor: 'white',
      boxSizing: 'border-box',
      fontFamily: 'Arial, sans-serif',
      position: 'relative'
    }}>
      {/* Decorative top-right corner */}
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: 0,
        height: 0,
        borderStyle: 'solid',
        borderWidth: '0 80px 80px 0',
        borderColor: 'transparent #F4824C transparent transparent'
      }} />

      {/* Evidence collection */}
      <ChevronFullBox labelText={<>Evidence collection to<br />support/negate hypothesis</>}>
        {studentData.evidence_collection}
      </ChevronFullBox>

      {/* Analysis and Synthesis with arrow */}
      <ChevronFullBox labelText="Analysis and Synthesis" showArrow>
        {studentData.analysis_synthesis}
      </ChevronFullBox>

      {/* Arrow before side-by-side boxes */}
      <div style={{ textAlign: 'center', margin: '-5px 0 5px 0' }}>
        <span style={{ fontSize: '28px', color: '#4A90A4', fontWeight: 'bold' }}>↓</span>
      </div>

      {/* What I know & What I need to do - Side by side with arrow */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '10px', position: 'relative' }}>
        <ChevronHalfBox labelText="What do I know?">
          {studentData.what_i_know}
        </ChevronHalfBox>
        <ChevronHalfBox labelText={<>What do I need to do?<br />What do I need to find out?</>}>
          {studentData.what_i_need_to_do}
        </ChevronHalfBox>
        {/* Horizontal arrow */}
        <div style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          fontSize: '28px',
          color: '#4A90A4',
          fontWeight: 'bold',
          zIndex: 1,
          backgroundColor: 'white',
          padding: '0 5px'
        }}>
          →
        </div>
      </div>

      {/* Arrow pointing down */}
      <div style={{ textAlign: 'center', margin: '-5px 0 5px 0' }}>
        <span style={{ fontSize: '28px', color: '#4A90A4', fontWeight: 'bold' }}>↓</span>
      </div>

      {/* Conclusion & Discussions - Side by side with arrow */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px', position: 'relative' }}>
        <ChevronHalfBox labelText="Conclusion">
          {studentData.conclusion}
        </ChevronHalfBox>
        <ChevronHalfBox labelText="Discussions">
          {studentData.discussions}
        </ChevronHalfBox>
        {/* Left-pointing arrow */}
        <div style={{
          position: 'absolute',
          right: '52%',
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: '28px',
          color: '#4A90A4',
          fontWeight: 'bold',
          zIndex: 1,
          backgroundColor: 'white',
          padding: '0 5px'
        }}>
          ←
        </div>
      </div>

      {/* Research task schedule */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          <div style={{
            backgroundColor: '#4A90A4',
            color: 'white',
            padding: '20px 15px',
            fontSize: '12px',
            fontWeight: 'bold',
            minWidth: '180px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center'
          }}>
            Research task schedule
          </div>
          <div style={{
            backgroundColor: '#B3D9E8',
            flex: 1,
            padding: '12px 15px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '6px 30px',
            fontSize: '10px',
            alignContent: 'center'
          }}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(day => (
              <div key={day} style={{ display: 'flex', gap: '5px' }}>
                <strong style={{ minWidth: '42px' }}>Day {day}:</strong>
                <span>{studentData.research_task_schedule?.[`day_${day}`] || '-'}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Page Number */}
      <div style={{
        position: 'absolute',
        bottom: '15mm',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: '11px',
        color: '#666',
        fontWeight: 'bold'
      }}>
        20
      </div>

      {/* Bottom corners */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: 0,
        height: 0,
        borderStyle: 'solid',
        borderWidth: '60px 0 0 60px',
        borderColor: 'transparent transparent transparent #F4824C'
      }} />
      <div style={{
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: 0,
        height: 0,
        borderStyle: 'solid',
        borderWidth: '60px 60px 0 0',
        borderColor: 'transparent #F4824C transparent transparent',
        opacity: 0.3
      }} />
    </div>
  );
};

export default PartCPage2;
